// colorChannelBars — Modern カラーパネルの「色付きグラデ横バー」の純ロジック
// (consult 2026-06-08).
//
// 背景: 数値テキスト入力 (R/G/B・H/S/V・H/S/L) を、各チャンネルが **そのチャンネルの
// 色グラデを背景に持つ横スライダ** に置換する (Classic ARGB バーの着想、ただし横)。
// バー UI は native `<input type="range">` の track 背景に `linear-gradient` を当てる
// 方式 (共有 ColorPanel 側)。本ファイルは DOM に触らない pure function だけを置き、
// vitest (node 環境) でグラデ計算 / フィールド定義をテストできるようにする。
//
// 2026-06-20: 共有カラーパネル抽出 (consult 2026-06-20) に伴い `layouts/modern/` から
// `presentation/color/` へ移設 (Classic/Modern 両 layout が共有 ColorPanel 経由で使う)。
//
// グラデ仕様 (consult 2026-06-08):
//   - RGB: R バー = 現在 G,B 固定で R 0→255 のグラデ (`rgb(0,G,B) → rgb(255,G,B)`)。G/B 同様。
//   - HSV: H = 色相レインボー (0→360)、S = 現在 H,V で無彩→純色、V = 現在 H,S で黒→明。
//   - HSL: H = レインボー、S = 現在 H,L で灰→純色、L = 現在 H,S で黒→中間色→白。
//   - alpha は一切扱わない (= カラーパネルは alpha を操作しない、§9.1)。

import { hslaToHsva, hsvaToRgba } from '@uiw/react-color';

import type { DisplayValues, NumericMode } from './colorWheel.js';

/** 1 チャンネル分のバー定義 (UI 非依存)。 */
export interface ChannelBar {
  /** React key / 内部識別子 (例: 'r' / 'h' / 'l')。 */
  readonly key: string;
  /** 短ラベル (例: 'R' / 'H' / 'L')。 */
  readonly label: string;
  /** a11y 用日本語ラベル (例: '赤' / '色相' / '明度')。 */
  readonly ariaLabel: string;
  /** 現在値 (整数)。 */
  readonly value: number;
  /** 入力下限 (= range の min)。 */
  readonly min: number;
  /** 入力上限 (= range の max)。 */
  readonly max: number;
  /** トラック背景にする CSS `linear-gradient(to right, ...)` 文字列。 */
  readonly gradient: string;
  /** この値を n に変えた DisplayValues を作る (= commit へ渡す)。 */
  readonly build: (n: number) => DisplayValues;
}

/** rgb(r,g,b) 文字列 (0..255 を整数 clamp)。 */
const rgb = (r: number, g: number, b: number): string =>
  `rgb(${clamp255(r)}, ${clamp255(g)}, ${clamp255(b)})`;

const clamp255 = (v: number): number => Math.max(0, Math.min(255, Math.round(v)));

/** linear-gradient(to right, ...stops) を組む。 */
const gradient = (...stops: ReadonlyArray<string>): string =>
  `linear-gradient(to right, ${stops.join(', ')})`;

/** 色相レインボー (H 0→360)。S/V/L は最大鮮やかさで固定して見やすく。 */
const hueRainbow = (): string =>
  gradient(
    'hsl(0, 100%, 50%)',
    'hsl(60, 100%, 50%)',
    'hsl(120, 100%, 50%)',
    'hsl(180, 100%, 50%)',
    'hsl(240, 100%, 50%)',
    'hsl(300, 100%, 50%)',
    'hsl(360, 100%, 50%)',
  );

/** HSV の (h,s,v) → rgb() 文字列 (s,v は 0..100)。 */
const hsvToCss = (h: number, s: number, v: number): string => {
  const { r, g, b } = hsvaToRgba({ h, s, v, a: 1 });
  return rgb(r, g, b);
};

/** HSL の (h,s,l) → rgb() 文字列 (s,l は 0..100)。 */
const hslToCss = (h: number, s: number, l: number): string => {
  const { r, g, b } = hsvaToRgba(hslaToHsva({ h, s, l, a: 1 }));
  return rgb(r, g, b);
};

/**
 * 現在の DisplayValues から、表記モードに応じた 3 本のバー定義を作る。
 * 各バーの gradient は **他 2 成分を現在値で固定** して 1 成分だけ振る。
 */
export const buildChannelBars = (d: DisplayValues): ReadonlyArray<ChannelBar> => {
  if (d.mode === 'rgb') {
    const { r, g, b } = d;
    return [
      {
        key: 'r',
        label: 'R',
        ariaLabel: '赤',
        value: r,
        min: 0,
        max: 255,
        gradient: gradient(rgb(0, g, b), rgb(255, g, b)),
        build: (n) => ({ ...d, r: n }),
      },
      {
        key: 'g',
        label: 'G',
        ariaLabel: '緑',
        value: g,
        min: 0,
        max: 255,
        gradient: gradient(rgb(r, 0, b), rgb(r, 255, b)),
        build: (n) => ({ ...d, g: n }),
      },
      {
        key: 'b',
        label: 'B',
        ariaLabel: '青',
        value: b,
        min: 0,
        max: 255,
        gradient: gradient(rgb(r, g, 0), rgb(r, g, 255)),
        build: (n) => ({ ...d, b: n }),
      },
    ];
  }
  if (d.mode === 'hsv') {
    const { h, s, v } = d;
    return [
      {
        key: 'h',
        label: 'H',
        ariaLabel: '色相',
        value: h,
        min: 0,
        max: 359, // 色相は循環 (360≡0)。colorWheel が % 360 で正規化するため上限 360 だと右端で 0 に wrap し左端へ戻る → 359 上限で回避。
        gradient: hueRainbow(),
        build: (n) => ({ ...d, h: n }),
      },
      {
        key: 's',
        label: 'S',
        ariaLabel: '彩度',
        value: s,
        min: 0,
        max: 100,
        // 無彩 (S=0) → 純色 (S=100)。V は現在値で固定。
        gradient: gradient(hsvToCss(h, 0, v), hsvToCss(h, 100, v)),
        build: (n) => ({ ...d, s: n }),
      },
      {
        key: 'v',
        label: 'V',
        ariaLabel: '明度',
        value: v,
        min: 0,
        max: 100,
        // 黒 (V=0) → 明 (V=100)。H,S は現在値で固定。
        gradient: gradient(hsvToCss(h, s, 0), hsvToCss(h, s, 100)),
        build: (n) => ({ ...d, v: n }),
      },
    ];
  }
  // hsl
  const { h, s, l } = d;
  return [
    {
      key: 'h',
      label: 'H',
      ariaLabel: '色相',
      value: h,
      min: 0,
      max: 359, // 色相は循環 (360≡0)。colorWheel が % 360 で正規化するため上限 360 だと右端で 0 に wrap し左端へ戻る → 359 上限で回避。
      gradient: hueRainbow(),
      build: (n) => ({ ...d, h: n }),
    },
    {
      key: 's',
      label: 'S',
      ariaLabel: '彩度',
      value: s,
      min: 0,
      max: 100,
      // 灰 (S=0) → 純色 (S=100)。L は現在値で固定。
      gradient: gradient(hslToCss(h, 0, l), hslToCss(h, 100, l)),
      build: (n) => ({ ...d, s: n }),
    },
    {
      key: 'l',
      label: 'L',
      ariaLabel: '輝度',
      value: l,
      min: 0,
      max: 100,
      // 黒 (L=0) → 中間色 (L=50) → 白 (L=100)。H,S は現在値で固定。
      gradient: gradient(hslToCss(h, s, 0), hslToCss(h, s, 50), hslToCss(h, s, 100)),
      build: (n) => ({ ...d, l: n }),
    },
  ];
};

// re-export 型はせず、ColorPanel から DisplayValues / NumericMode を別途 import する。
export type { DisplayValues, NumericMode };
